import { useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { isVideoLibraryPath } from '../common/utils/video-route-utils';

interface StagingLeaveGuard {
  pendingPath: string | null;
  requestLeave: (path: string) => void;
  leave: (path: string) => void;
  stay: () => void;
}

export function useStagingLeaveGuard(
  isActive: boolean,
  onReturnToLibrary: () => void
): StagingLeaveGuard {
  const navigate = useNavigate();
  const [requestedPath, setRequestedPath] = useState<string | null>(null);
  const isLeaving = useRef(false);
  const returnToLibrary = useRef(onReturnToLibrary);

  useEffect(() => {
    returnToLibrary.current = onReturnToLibrary;
  }, [onReturnToLibrary]);

  const blocker = useBlocker(({ nextLocation: { pathname } }) => {
    if (!isActive || isLeaving.current) {
      return false;
    }

    if (isVideoLibraryPath(pathname)) {
      returnToLibrary.current();
      return false;
    }

    return true;
  });

  const blockedPath =
    blocker.state === 'blocked'
      ? `${blocker.location.pathname}${blocker.location.search}${blocker.location.hash}`
      : null;

  const leave = (path: string) => {
    isLeaving.current = true;
    setRequestedPath(null);
    blocker.reset?.();
    navigate(path);
  };

  return {
    pendingPath: requestedPath ?? blockedPath,
    requestLeave: (path) => (isActive ? setRequestedPath(path) : leave(path)),
    leave,
    stay: () => {
      setRequestedPath(null);
      blocker.reset?.();
    },
  };
}
